// Answers a <video>'s requests for a large encrypted file inside the service worker. The page
// registers the file under a random id and points the video at /__media/<id>. Each request names
// a byte range of the plain file. Only the encrypted chunks holding that range are fetched from
// storage, opened and sent back, so playback starts at once and seeking skips ahead.
//
// The desktop app does the same in Rust (apps/desktop/src-tauri/src/media_stream.rs), because the
// macOS webview has no service workers.

import {
  chunkCount,
  decryptStream,
  isChunked,
  parseRange,
  type RangeFetcher,
  urlRangeFetcher,
} from "@rhema/client/chunked";

export const STREAM_PREFIX = "/__media/";
/** The most plain bytes one answer carries. Players ask again for the rest. */
export const MAX_ANSWER = 8 * 1024 * 1024;

/** What the worker needs to play one file. The key never leaves this device. */
export type StreamEntry = {
  /** Storage key, used to ask the API for a fresh download URL. */
  key: string;
  /** A presigned URL to start with, so the first request needs no API call. */
  url?: string;
  file_key: string;
  iv: string;
  size: number;
  chunk_size: number;
  mime: string;
  sha256: string;
  version: number;
};

export function streamId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** The id in /__media/<id>, or null for any other path. */
export function idFromPath(pathname: string): string | null {
  if (!pathname.startsWith(STREAM_PREFIX)) return null;
  const id = pathname.slice(STREAM_PREFIX.length);
  return /^[0-9a-f]{32}$/.test(id) ? id : null;
}

/** Only audio and video are served, so a file can never be shown as a page. */
function safeType(mime: string): string {
  return mime.startsWith("video/") || mime.startsWith("audio/") ? mime : "application/octet-stream";
}

export type ServeDeps = {
  lookup(id: string): Promise<StreamEntry | null>;
  /** Fetches stored bytes for an entry. Defaults to ranged GETs on its URL, refreshed through the API. */
  fetcher?(entry: StreamEntry): RangeFetcher;
  fetch?: typeof fetch;
};

/** The default fetcher: the entry's URL first, then a fresh one from the API when it expires. */
export function apiFetcher(entry: StreamEntry, doFetch: typeof fetch = (...a) => fetch(...a)): RangeFetcher {
  let first = entry.url;
  return urlRangeFetcher(async () => {
    if (first) {
      const url = first;
      first = undefined;
      return url;
    }
    const res = await doFetch(`/api/media/url?key=${encodeURIComponent(entry.key)}&v=2`, {
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error("This file could not be downloaded. Try again.");
    return ((await res.json()) as { url: string }).url;
  }, doFetch);
}

function empty(status: number, headers: Record<string, string> = {}): Response {
  return new Response(null, { status, headers: { "Cache-Control": "no-store", ...headers } });
}

export async function serveMedia(request: Request, deps: ServeDeps): Promise<Response> {
  const id = idFromPath(new URL(request.url).pathname);
  if (!id) return empty(404);
  const entry = await deps.lookup(id).catch(() => null);
  if (!entry || !isChunked(entry)) return empty(404);
  const size = entry.size;
  const range = parseRange(request.headers.get("Range"), size);
  if (range === "unsatisfiable") return empty(416, { "Content-Range": `bytes */${size}` });
  const type = safeType(entry.mime);
  if (size === 0)
    return new Response(new Uint8Array(0), {
      status: 200,
      headers: { "Content-Type": type, "Content-Length": "0" },
    });
  const [start, askedEnd] = range ?? [0, size - 1];
  const end = Math.min(askedEnd, start + MAX_ANSWER - 1);
  const fetcher = deps.fetcher?.(entry) ?? apiFetcher(entry, deps.fetch);
  const whole = range === null && end === size - 1;
  const body = decryptStream(entry, fetcher, {
    start,
    end,
    window: Math.min(4, chunkCount({ size, chunkSize: entry.chunk_size })),
  });
  const headers: Record<string, string> = {
    "Content-Type": type,
    "Content-Length": String(end - start + 1),
    "Accept-Ranges": "bytes",
    "Cache-Control": "no-store",
  };
  if (!whole) headers["Content-Range"] = `bytes ${start}-${end}/${size}`;
  return new Response(body, { status: whole ? 200 : 206, headers });
}

/** Files the worker may play, by id. Old ones go once there are too many. */
export class StreamRegistry {
  private readonly entries = new Map<string, StreamEntry>();
  constructor(private readonly limit = 64) {}

  set(id: string, entry: StreamEntry): void {
    this.entries.delete(id);
    this.entries.set(id, entry);
    while (this.entries.size > this.limit) {
      const oldest = this.entries.keys().next().value as string;
      this.entries.delete(oldest);
    }
  }
  get(id: string): StreamEntry | null {
    return this.entries.get(id) ?? null;
  }
  delete(id: string): void {
    this.entries.delete(id);
  }
}

export const REGISTER = "rhema-media-register";
export const UNREGISTER = "rhema-media-unregister";
export const LOOKUP = "rhema-media-lookup";
